/**
 * The page's one visual language, as plain SVG drawn from the model's output:
 * a take is a bar scaled to seconds, the used portion is filled, a keeper is
 * the bar with something filled. Each cost bucket owns a hue, and every figure
 * in a section is drawn in that section's hue.
 */

import type { CSSProperties } from 'react'

export const FIG_W = 720
const FIELD = 'rgba(255,255,255,0.10)'
const MUTE = 'rgba(255,255,255,0.55)'
const BG = '#141414'

/** The three cost buckets. `tone` is the full hue; `on` is text that sits on it. */
export const BUCKET = {
  gen: { label: 'Generation', tone: '#5cd889', on: '#06230f' },
  art: { label: 'Personnel', tone: '#6fa3ff', on: '#06173a' },
  cln: { label: 'Cleanup', tone: '#ff9a5c', on: '#3a1604' },
} as const
export type Bucket = keyof typeof BUCKET

const fmtMins = (min: number) => (min < 1 ? `${Math.round(min * 60)} s` : `${Math.round(min * 10) / 10} min`)

const alpha = (hex: string, a: number) => {
  const n = parseInt(hex.slice(1), 16)
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`
}

const Label = ({
  x,
  y,
  children,
  anchor = 'start',
  fill = MUTE,
}: {
  x: number
  y: number
  children: string
  anchor?: 'start' | 'end'
  fill?: string
}) => (
  <text x={x} y={y} fontSize={12} textAnchor={anchor} fill={fill} className="font-mono">
    {children}
  </text>
)

/** One take: a bar `secs` long, with `used` seconds filled in the tone. */
function Take({
  x,
  y,
  w,
  h,
  secs,
  used,
  fill,
  stroke,
  tone,
  style,
  className,
  tip,
  usedTip,
}: {
  x: number
  y: number
  w: number
  h: number
  secs: number
  used: number
  fill: string
  stroke?: string
  tone: string
  style?: CSSProperties
  className?: string
  tip?: string
  usedTip?: string
}) {
  const usedW = (w / secs) * Math.min(used, secs)
  const ticks = h >= 20 ? Array.from({ length: Math.max(0, Math.ceil(secs) - 1) }, (_, i) => i + 1) : []
  return (
    <g style={style} className={className}>
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        rx={3}
        fill={fill}
        stroke={stroke}
        strokeWidth={stroke ? 1.5 : 0}
        data-tip={tip}
      />
      {used > 0 && <rect x={x} y={y} width={usedW} height={h} rx={3} fill={tone} data-tip={usedTip ?? tip} />}
      {ticks.map(t => (
        <line key={t} x1={x + (w / secs) * t} x2={x + (w / secs) * t} y1={y} y2={y + h} stroke={BG} strokeWidth={1} />
      ))}
    </g>
  )
}

/** Runtime with the AI share filled. */
export function RuntimeFigure({ runtime, aiMin, aiPct }: { runtime: number; aiMin: number; aiPct: number }) {
  const aiW = (FIG_W * aiPct) / 100
  const { tone, on } = BUCKET.gen
  return (
    <svg viewBox={`0 0 ${FIG_W} 36`} role="img" aria-label="Runtime split" className="block h-auto w-full">
      <rect
        x={0}
        y={4}
        width={FIG_W}
        height={26}
        rx={5}
        fill={FIELD}
        data-tip={`${fmtMins(runtime - aiMin)} of live action or other footage: not priced here`}
      />
      <rect
        x={0}
        y={4}
        width={aiW}
        height={26}
        rx={5}
        fill={tone}
        data-tip={`${fmtMins(aiMin)} of AI footage on screen: what this budget prices`}
      />
      {aiW > 80 && (
        <Label x={10} y={21} fill={on}>
          {`${Math.round(aiMin)} min AI`}
        </Label>
      )}
      {FIG_W - aiW > 100 && (
        <Label x={FIG_W - 10} y={21} anchor="end">
          {`${Math.round(runtime - aiMin)} min live`}
        </Label>
      )}
    </svg>
  )
}

/** One take at the scale of 30 s = full width, optionally with its used seconds. */
export function TakeFigure({
  clipLen,
  used,
  label,
  tip,
  usedTip,
}: {
  clipLen: number
  used: number
  label: string
  tip: string
  usedTip?: string
}) {
  const scale = FIG_W / 30
  const { tone } = BUCKET.gen
  return (
    <svg viewBox={`0 0 ${FIG_W} 34`} role="img" aria-label={label} className="block h-auto w-full">
      <Take
        x={0}
        y={4}
        w={clipLen * scale}
        h={26}
        secs={clipLen}
        used={used}
        fill={alpha(tone, 0.16)}
        tone={tone}
        tip={tip}
        usedTip={usedTip}
      />
      <Label x={clipLen * scale + 10} y={22}>
        {label}
      </Label>
    </svg>
  )
}

/**
 * All the takes behind one keeper. The last one is the keeper (used seconds
 * filled). With `colored`, finals are outlined and drafts are flat.
 */
export function TakesGrid({
  tries,
  clipLen,
  used,
  drafts,
  colored,
  dense = true,
  intro = false,
  label,
  tipFor,
}: {
  tries: number
  clipLen: number
  used: number
  drafts: number
  colored: boolean
  dense?: boolean
  /** Staggered appearance on first paint (hero only). */
  intro?: boolean
  label: string
  /** Hover label for take `i` (0-based); `kind` says which box it is. */
  tipFor?: (i: number, kind: 'draft' | 'final' | 'shot') => string
}) {
  const { tone } = BUCKET.gen
  const cols = Math.min(tries, 10)
  const rows = Math.ceil(tries / cols)
  const gap = dense ? 6 : 8
  const w = (FIG_W - (cols - 1) * gap) / cols
  const h = dense ? (tries > 40 ? 8 : tries > 20 ? 10 : 14) : tries > 40 ? 10 : tries > 20 ? 16 : 22
  const rowGap = dense ? 4 : 7
  const height = rows * (h + rowGap) - rowGap
  return (
    // overflow-visible: the outlined finals sit on the viewBox edge and their stroke was being clipped.
    <svg
      viewBox={`0 0 ${FIG_W} ${height}`}
      role="img"
      aria-label={label}
      className="block h-auto w-full overflow-visible"
    >
      {Array.from({ length: tries }, (_, i) => {
        const isKeeper = i === tries - 1
        const isFinal = i >= drafts
        return (
          <Take
            key={i}
            x={(i % cols) * (w + gap)}
            y={Math.floor(i / cols) * (h + rowGap)}
            w={w}
            h={h}
            secs={clipLen}
            used={isKeeper ? used : 0}
            fill={colored ? (isFinal ? alpha(tone, 0.08) : alpha(tone, 0.22)) : FIELD}
            stroke={colored && isFinal ? alpha(tone, 0.8) : undefined}
            tone={tone}
            tip={tipFor?.(i, isKeeper ? 'shot' : isFinal ? 'final' : 'draft')}
            className={
              intro
                ? 'motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-1 motion-safe:fill-mode-both motion-safe:duration-500'
                : undefined
            }
            style={intro ? { animationDelay: `${Math.min(i * 35, 1500)}ms` } : undefined}
          />
        )
      })}
    </svg>
  )
}

/** Calendar months, filled by how far the schedule reaches. Capped at 36 boxes: a typed extreme must not build 300k SVG nodes. */
export function ScheduleFigure({ months, team }: { months: number; team: number }) {
  const maxM = Math.min(36, Math.max(12, Math.ceil(months)))
  const mw = FIG_W / maxM
  const { tone } = BUCKET.art
  return (
    <svg viewBox={`0 0 ${FIG_W} 46`} role="img" aria-label="Schedule" className="block h-auto w-full">
      {Array.from({ length: maxM }, (_, m) => (
        <rect
          key={m}
          x={m * mw + 1}
          y={4}
          width={mw - 2}
          height={22}
          rx={3}
          fill={m + 1 <= months ? tone : m < months ? alpha(tone, 0.45) : FIELD}
          data-tip={
            m + 1 <= months
              ? `Month ${m + 1}: ${team} creator${team === 1 ? '' : 's'} working`
              : m < months
                ? `Month ${m + 1}: the last ${Math.round((months - m) * 100)}% of a month`
                : `Month ${m + 1}: not needed at this pace`
          }
        />
      ))}
      <Label x={0} y={42}>
        month 1
      </Label>
      <Label x={FIG_W} y={42} anchor="end">
        {`month ${maxM}${months > maxM ? '+' : ''}`}
      </Label>
    </svg>
  )
}

/**
 * The shots of the whole piece, in the hue they were made in (generation),
 * with the share that needs a cleanup pass turned to the cleanup hue. One box
 * is one shot up to 100; past that each box stands for `total / 100` shots.
 */
export function ShotsFigure({ total, pct, cleanupTip }: { total: number; pct: number; cleanupTip: string }) {
  const n = total > 100 ? 100 : Math.round(total)
  const cols = 25
  const gap = 4
  const sq = (FIG_W - (cols - 1) * gap) / cols
  const rows = Math.max(1, Math.ceil(n / cols))
  const flagged = Math.round((n * pct) / 100)
  const perBox = total > 100 ? `About ${Math.round(total / 100)} finished shots:` : 'One finished shot:'
  return (
    <svg
      viewBox={`0 0 ${FIG_W} ${rows * (sq + gap) - gap}`}
      role="img"
      aria-label="Shots needing cleanup"
      className="block h-auto w-full"
    >
      <defs>
        <pattern id="cln-hatch" width={6} height={6} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <rect width={6} height={6} fill={alpha(BUCKET.gen.tone, 0.55)} />
          <rect width={2.5} height={6} fill={BUCKET.cln.tone} />
        </pattern>
      </defs>
      {Array.from({ length: n }, (_, i) => {
        const x = (i % cols) * (sq + gap)
        const y = Math.floor(i / cols) * (sq + gap)
        const cleanup = i < flagged
        return (
          <rect
            key={i}
            x={x}
            y={y}
            width={sq}
            height={sq * 0.6}
            rx={3}
            fill={cleanup ? 'url(#cln-hatch)' : alpha(BUCKET.gen.tone, 0.55)}
            stroke={cleanup ? BUCKET.cln.tone : undefined}
            strokeWidth={cleanup ? 1.5 : 0}
            data-tip={`${perBox} ${cleanup ? cleanupTip : 'ships as generated, no cleanup'}`}
          />
        )
      })}
    </svg>
  )
}

/** One creator's day: the shots they get through, in the people hue. */
export function DayFigure({ shotsPerDay, max = 15 }: { shotsPerDay: number; max?: number }) {
  const gap = 6
  const w = (FIG_W - (max - 1) * gap) / max
  const h = w * 0.6
  const { tone } = BUCKET.art
  return (
    <svg
      viewBox={`0 0 ${FIG_W} ${h + 24}`}
      role="img"
      aria-label="Shots per creator per day"
      className="block h-auto w-full"
    >
      {Array.from({ length: max }, (_, i) => {
        const fill = Math.max(0, Math.min(1, shotsPerDay - i))
        return (
          <g key={i}>
            <rect
              x={i * (w + gap)}
              y={0}
              width={w}
              height={h}
              rx={3}
              fill={FIELD}
              data-tip={
                fill > 0
                  ? `Finished shot ${i + 1} of a creator's day, every generation behind it included`
                  : `Room for shot ${i + 1}: drag the slider up if your creators are faster`
              }
            />
            {fill > 0 && (
              <rect
                x={i * (w + gap)}
                y={0}
                width={w * fill}
                height={h}
                rx={3}
                fill={tone}
                data-tip={`Finished shot ${i + 1} of a creator's day, every generation behind it included`}
              />
            )}
          </g>
        )
      })}
      <Label x={0} y={h + 18}>
        one creator · one day
      </Label>
    </svg>
  )
}
